/** A sortable table and a page-number pager. */

import type { ReactNode } from 'react';

export interface Column<TRow> {
  key: string;
  label: string;
  /** Sortable columns must name a field the API can sort by. */
  sortable?: boolean;
  num?: boolean;
  render: (row: TRow) => ReactNode;
}

export interface SortState {
  key: string;
  dir: 'ASC' | 'DESC';
}

export function DataTable<TRow>({ columns, rows, rowKey, sort, onSort, empty }: {
  columns: readonly Column<TRow>[];
  rows: readonly TRow[];
  rowKey: (row: TRow, index: number) => string;
  sort?: SortState;
  onSort?: (key: string) => void;
  empty?: string;
}) {
  // A header is still rendered when there are no rows: losing the sort
  // controls the moment a filter matches nothing makes the table feel
  // broken exactly when the user needs to widen the filter.
  return (
    <div className="table-wrap">
      <table className="grid-table">
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort?.key === column.key;
              const clickable = column.sortable && onSort;
              return (
                <th
                  key={column.key}
                  className={[column.num ? 'num' : '', clickable ? 'sortable' : '']
                    .filter(Boolean).join(' ')}
                  {...(clickable ? {
                    onClick: () => onSort(column.key),
                    role: 'button',
                    tabIndex: 0,
                    onKeyDown: (event) => {
                      if (event.key === 'Enter' || event.key === ' ') onSort(column.key);
                    },
                  } : {})}
                >
                  {column.label}
                  {active && <span className="caret">{sort.dir === 'ASC' ? '▲' : '▼'}</span>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="table-empty">
                {empty ?? 'Няма резултати за тези филтри.'}
              </td>
            </tr>
          ) : rows.map((row, index) => (
            <tr key={rowKey(row, index)}>
              {columns.map((column) => (
                /* the label rides along so the phone layout — which hides
                   the header row and stacks each row as a card — can print
                   it beside the value */
                <td
                  key={column.key}
                  className={column.num ? 'num' : undefined}
                  {...(column.label ? { 'data-label': column.label } : {})}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Page numbers, not just prev/next.
 *
 * Prev/next alone cannot reach the middle of a result set, and a filtered
 * procurement list still runs to hundreds of pages. A window around the
 * current page plus first and last keeps the control a fixed width however
 * long the list is.
 */
export function Pager({ page, pageSize, total, onGo, window: span = 2 }: {
  page: number; pageSize: number; total: number;
  onGo: (page: number) => void; window?: number;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pages - 1);
  const from = current * pageSize;

  const wanted = new Set<number>([0, pages - 1]);
  for (let candidate = current - span; candidate <= current + span; candidate += 1) {
    if (candidate >= 0 && candidate < pages) wanted.add(candidate);
  }
  const ordered = [...wanted].sort((a, b) => a - b);

  return (
    <div className="pager">
      <span>
        {total > 0
          ? `Показани ${from + 1}–${Math.min(from + pageSize, total)} от ${total.toLocaleString('bg-BG')}`
          : 'Няма резултати'}
      </span>
      <span className="pager-controls">
        <button type="button" className="btn" disabled={current === 0}
          onClick={() => onGo(current - 1)}>←</button>
        {ordered.map((candidate, index) => (
          <span key={candidate}>
            {/* a gap in the sequence is an elision, not a missing button */}
            {index > 0 && candidate - ordered[index - 1]! > 1 && (
              <span className="pager-gap">…</span>
            )}
            <button
              type="button"
              className={candidate === current ? 'btn btn-page btn-page-on' : 'btn btn-page'}
              onClick={candidate === current ? undefined : () => onGo(candidate)}
            >
              {candidate + 1}
            </button>
          </span>
        ))}
        <button type="button" className="btn" disabled={current >= pages - 1}
          onClick={() => onGo(current + 1)}>→</button>
      </span>
    </div>
  );
}
